import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  StreamProtocol,
  useWhepLiveStream,
  WhepLiveStreamStatus,
} from "../AlertsUtils/useWhepLiveStream";
import type { LiveViewFailure } from "../AlertsUtils/liveViewFailure";
import { useIdleTimer } from "../AlertsUtils/useIdleTimer";
import {
  IDLE_GRACE_MS,
  VIDEO_WALL_IDLE_MS,
} from "../AlertsUtils/liveViewIdleConfig";
import {
  useIsTalkingToCamera,
  useTalkingCamerasStore,
} from "../../../common/TalkDown/talkingCamerasStore";
import { useListenInHoldStore } from "../listenInHoldStore";

/**
 * Persistent video-wall stream registry.
 *
 * Problem: the wall renders the same camera in different React subtrees per
 * layout (mini panel, Focus+Grid spotlight vs side grid, Equal Grid). Switching
 * modes/panels unmounts one subtree and mounts another, which — when each tile
 * owned its own <video> + player — tore the player down and forced a fresh
 * stream-session request (cold-start), churning the lease and resetting the
 * MediaMTX HLS muxer (→ segment 404s, "in and out").
 *
 * Fix: own the player + <video> here, keyed by camera id, for as long as the
 * camera is in the active set. Each <video> is created IMPERATIVELY
 * (document.createElement) — never rendered by React — and moved into the
 * current tile slot with appendChild. appendChild RE-PARENTS the same DOM node
 * (it does not recreate it), so srcObject / the attached hls.js media survive
 * the move; the player keeps running across a layout change with no teardown,
 * no re-request, no muxer reset.
 *
 * (This is why a React portal does NOT work here: changing a portal's container
 * unmounts + remounts its content, recreating the <video> and orphaning the
 * stream. The element must be moved imperatively.)
 *
 * Active set is DERIVED from slot registrations with a short removal grace, so a
 * mode/panel toggle's unmount→remount keeps the player alive (same camera, new
 * slot) while a paged-away camera is still reaped.
 */

const REMOVE_GRACE_MS = 1500;
const VIDEO_CLASS = "absolute inset-0 h-full w-full rounded object-contain";

interface WallStreamsContextValue {
  /** Tiles call this via a ref callback to claim/release the slot for a camera. */
  registerSlot: (cameraId: string, node: HTMLElement | null) => void;
  /** Current play status for a camera (drives the tile badge/overlay). */
  useStreamStatus: (cameraId: string) => WhepLiveStreamStatus;
  /** Why this camera's stream failed (null while loading/playing). */
  useStreamFailure: (cameraId: string) => LiveViewFailure | null;
  /** "Try again": re-run this camera's play() (fresh mint). */
  retryStream: (cameraId: string) => void;
  /** Wall-wide "Still watching?" grace window (ENG-2149): tiles still play. */
  idlePrompting: boolean;
  /** Wall-wide idle pause (ENG-1132): every tile is torn down together. */
  idlePaused: boolean;
  /** "I'm still watching" / "Resume" — back to active; from paused this
   * re-mints every tile. */
  resumeFromIdle: () => void;
  /** Listen-in (ENG-2136): does this camera's stream carry audio? */
  useStreamHasAudio: (cameraId: string) => boolean;
  /** The one camera on the wall whose audio is unmuted, if any. */
  listeningCameraId: string | null;
  /** Unmute this camera (muting every other tile) or mute it again. */
  toggleListen: (cameraId: string) => void;
}

const WallStreamsContext = createContext<WallStreamsContextValue | null>(null);

export const useWallStreams = (): WallStreamsContextValue => {
  const ctx = useContext(WallStreamsContext);
  if (!ctx) {
    throw new Error("useWallStreams must be used within a WallStreamsProvider");
  }
  return ctx;
};

interface PersistentStreamProps {
  cameraId: string;
  protocol: StreamProtocol;
  lowLatency: boolean;
  enabled: boolean;
  idlePaused: boolean;
  slotNode: HTMLElement | null;
  onStatusChange: (cameraId: string, status: WhepLiveStreamStatus) => void;
  onHasAudioChange: (cameraId: string, hasAudio: boolean) => void;
  onFailureChange: (cameraId: string, failure: LiveViewFailure | null) => void;
  /** Register (or clear, with null) this camera's retry callback. */
  registerRetry: (cameraId: string, retry: (() => void) | null) => void;
  /** This tile is the wall's single unmuted camera. */
  listening: boolean;
}

const HOLDER_STYLE: React.CSSProperties = {
  position: "absolute",
  width: 0,
  height: 0,
  overflow: "hidden",
  pointerEvents: "none",
  opacity: 0,
};

const PersistentStream: React.FC<PersistentStreamProps> = ({
  cameraId,
  protocol,
  lowLatency,
  enabled,
  idlePaused,
  slotNode,
  onStatusChange,
  onHasAudioChange,
  onFailureChange,
  registerRetry,
  listening,
}) => {
  // The <video> is created once, imperatively, and lives in this ref for the
  // lifetime of the component (i.e. as long as the camera is active). React
  // never renders it, so it is never recreated — only re-parented via
  // appendChild below.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  if (!videoRef.current && typeof document !== "undefined") {
    const v = document.createElement("video");
    v.autoplay = true;
    v.muted = true;
    v.playsInline = true;
    v.className = VIDEO_CLASS;
    videoRef.current = v;
  }
  // A per-stream offscreen holder that is always in the DOM (rendered below).
  // The <video> parks here whenever no tile slot is displaying the camera.
  const holderRef = useRef<HTMLDivElement>(null);

  // Attach the <video> to the DOM (current slot, else the holder) in a LAYOUT
  // effect — this runs before the player hook's passive play() effect, so the
  // element is already in the document when WHEP/HLS attaches + play()s. (A
  // detached element at play() time was why the stream never started.)
  // appendChild re-parents the SAME node, preserving srcObject / hls media.
  useLayoutEffect(() => {
    const v = videoRef.current;
    const target = slotNode ?? holderRef.current;
    if (v && target && v.parentNode !== target) {
      target.appendChild(v);
    }
  }, [slotNode]);

  // Bind the player to the stable element. videoRef.current never changes, so
  // the srcObject / hls attachment is preserved across moves.
  const { status, failure, retry, hasAudio } = useWhepLiveStream(
    cameraId,
    enabled,
    videoRef,
    protocol,
    lowLatency,
    idlePaused
  );

  useEffect(() => {
    onStatusChange(cameraId, status);
  }, [cameraId, status, onStatusChange]);

  useEffect(() => {
    onFailureChange(cameraId, failure ?? null);
  }, [cameraId, failure, onFailureChange]);

  useEffect(() => {
    registerRetry(cameraId, retry ?? null);
    return () => registerRetry(cameraId, null);
  }, [cameraId, retry, registerRetry]);

  useEffect(() => {
    onHasAudioChange(cameraId, Boolean(hasAudio));
  }, [cameraId, hasAudio, onHasAudioChange]);

  // Listen-in (ENG-2136): muted unless this is the wall's one listening tile,
  // and always muted while the operator talks down to this camera.
  const talking = useIsTalkingToCamera(cameraId);
  const muted = !listening || talking;
  useEffect(() => {
    const v = videoRef.current;
    if (v && v.muted !== muted) v.muted = muted;
  }, [muted]);

  // Remove the element from the DOM when this camera leaves the active set.
  useEffect(() => {
    const v = videoRef.current;
    return () => {
      v?.remove();
    };
  }, []);

  return <div ref={holderRef} aria-hidden style={HOLDER_STYLE} />;
};

interface WallStreamsProviderProps {
  protocol: StreamProtocol;
  lowLatency: boolean;
  /** Master gate — false (no panel open) tears every player down. */
  enabled: boolean;
  children: React.ReactNode;
}

export const WallStreamsProvider: React.FC<WallStreamsProviderProps> = ({
  protocol,
  lowLatency,
  enabled,
  children,
}) => {
  const [slots, setSlots] = useState<Record<string, HTMLElement | null>>({});
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<
    Record<string, WhepLiveStreamStatus>
  >({});
  // Per-camera failure reason (why a tile shows its error overlay), and each
  // live player's retry, for the tile's "Try again".
  const [failures, setFailures] = useState<
    Record<string, LiveViewFailure | null>
  >({});
  const retries = useRef<Record<string, () => void>>({});
  // Listen-in (ENG-2136): per-camera "stream carries audio", and the single
  // camera whose audio is on — one at a time, so unmuting a tile implicitly
  // mutes whichever tile was audible before.
  const [hasAudioById, setHasAudioById] = useState<Record<string, boolean>>({});
  const [listeningCameraId, setListeningCameraId] = useState<string | null>(
    null
  );
  const removalTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );
  // One idle timer for the whole wall (ENG-1132 / ENG-2149): any interaction
  // anywhere on the document resets it; when it fires the panel shows a single
  // "Still watching?" prompt, and if nobody confirms within the grace period
  // every tile pauses together. Only armed once at least one tile is actually
  // PLAYING (cold starts don't count), and kept armed through the pause so the
  // pause isn't cleared out from under the players. One window for every
  // viewer — staff and uncapped plans included (ENG-2149).
  const wallStreaming = activeIds.some(
    (id) => statuses[id] === "playing" || statuses[id] === "idle_paused"
  );
  // Talking to, or holding listen-in on, any wall camera is engagement: it
  // stops the wall clock and dismisses a showing prompt. A listen-in toggle
  // left on does not (it would keep an unattended wall streaming forever).
  const talkingHolds = useTalkingCamerasStore((s) => s.holds);
  const listenInHoldCameraId = useListenInHoldStore((s) => s.holdCameraId);
  const talkingIds = Object.values(talkingHolds);
  const wallHeld = activeIds.some(
    (id) => talkingIds.includes(id) || listenInHoldCameraId === id
  );
  const {
    idle: idlePaused,
    prompting: idlePrompting,
    reset: resumeFromIdle,
  } = useIdleTimer(
    enabled && wallStreaming,
    VIDEO_WALL_IDLE_MS,
    IDLE_GRACE_MS,
    wallHeld
  );

  const clearRemoval = (cameraId: string) => {
    const t = removalTimers.current[cameraId];
    if (t) {
      clearTimeout(t);
      delete removalTimers.current[cameraId];
    }
  };

  const registerSlot = useCallback(
    (cameraId: string, node: HTMLElement | null) => {
      if (node) {
        clearRemoval(cameraId);
        setSlots((prev) =>
          prev[cameraId] === node ? prev : { ...prev, [cameraId]: node }
        );
        setActiveIds((prev) =>
          prev.includes(cameraId) ? prev : [...prev, cameraId]
        );
        return;
      }
      setSlots((prev) => {
        if (!(cameraId in prev)) return prev;
        const next = { ...prev };
        delete next[cameraId];
        return next;
      });
      if (!removalTimers.current[cameraId]) {
        removalTimers.current[cameraId] = setTimeout(() => {
          delete removalTimers.current[cameraId];
          setActiveIds((prev) => prev.filter((id) => id !== cameraId));
          setStatuses((prev) => {
            if (!(cameraId in prev)) return prev;
            const next = { ...prev };
            delete next[cameraId];
            return next;
          });
          setHasAudioById((prev) => {
            if (!(cameraId in prev)) return prev;
            const next = { ...prev };
            delete next[cameraId];
            return next;
          });
          setFailures((prev) => {
            if (!(cameraId in prev)) return prev;
            const next = { ...prev };
            delete next[cameraId];
            return next;
          });
        }, REMOVE_GRACE_MS);
      }
    },
    []
  );

  const onStatusChange = useCallback(
    (cameraId: string, status: WhepLiveStreamStatus) => {
      setStatuses((prev) =>
        prev[cameraId] === status ? prev : { ...prev, [cameraId]: status }
      );
    },
    []
  );

  const onHasAudioChange = useCallback(
    (cameraId: string, hasAudio: boolean) => {
      setHasAudioById((prev) =>
        prev[cameraId] === hasAudio ? prev : { ...prev, [cameraId]: hasAudio }
      );
    },
    []
  );

  const onFailureChange = useCallback(
    (cameraId: string, failure: LiveViewFailure | null) => {
      // Compare by value: a new-but-equal object must not re-render the
      // wall (and re-fire this effect) forever.
      setFailures((prev) => {
        const cur = prev[cameraId];
        if (
          cameraId in prev &&
          (cur ?? null)?.reason === failure?.reason &&
          (cur ?? null)?.detail === failure?.detail
        ) {
          return prev;
        }
        return { ...prev, [cameraId]: failure };
      });
    },
    []
  );

  const registerRetry = useCallback(
    (cameraId: string, retry: (() => void) | null) => {
      if (retry) retries.current[cameraId] = retry;
      else delete retries.current[cameraId];
    },
    []
  );

  const useStreamFailure = useCallback(
    (cameraId: string): LiveViewFailure | null => failures[cameraId] ?? null,
    [failures]
  );

  const retryStream = useCallback((cameraId: string) => {
    retries.current[cameraId]?.();
  }, []);

  const useStreamHasAudio = useCallback(
    (cameraId: string): boolean => hasAudioById[cameraId] === true,
    [hasAudioById]
  );

  const toggleListen = useCallback((cameraId: string) => {
    setListeningCameraId((prev) => (prev === cameraId ? null : cameraId));
  }, []);

  // A stream that stops playing (paged away, idle pause, restart, error)
  // drops its listen state — every stream starts muted.
  useEffect(() => {
    if (listeningCameraId && statuses[listeningCameraId] !== "playing") {
      setListeningCameraId(null);
    }
  }, [listeningCameraId, statuses]);

  const useStreamStatus = useCallback(
    (cameraId: string): WhepLiveStreamStatus => statuses[cameraId] ?? "idle",
    [statuses]
  );

  useEffect(() => {
    if (enabled) return;
    Object.keys(removalTimers.current).forEach(clearRemoval);
    setActiveIds([]);
    setSlots({});
    setStatuses({});
    setHasAudioById({});
    setFailures({});
    setListeningCameraId(null);
  }, [enabled]);

  useEffect(
    () => () => {
      Object.values(removalTimers.current).forEach(clearTimeout);
      removalTimers.current = {};
    },
    []
  );

  const ctx = useMemo<WallStreamsContextValue>(
    () => ({
      registerSlot,
      useStreamStatus,
      useStreamFailure,
      retryStream,
      idlePrompting,
      idlePaused,
      resumeFromIdle,
      useStreamHasAudio,
      listeningCameraId,
      toggleListen,
    }),
    [
      registerSlot,
      useStreamStatus,
      useStreamFailure,
      retryStream,
      idlePrompting,
      idlePaused,
      resumeFromIdle,
      useStreamHasAudio,
      listeningCameraId,
      toggleListen,
    ]
  );

  return (
    <WallStreamsContext.Provider value={ctx}>
      {enabled &&
        activeIds.map((cameraId) => (
          <PersistentStream
            key={cameraId}
            cameraId={cameraId}
            protocol={protocol}
            lowLatency={lowLatency}
            enabled={enabled}
            idlePaused={idlePaused}
            slotNode={slots[cameraId] ?? null}
            onStatusChange={onStatusChange}
            onHasAudioChange={onHasAudioChange}
            onFailureChange={onFailureChange}
            registerRetry={registerRetry}
            listening={listeningCameraId === cameraId}
          />
        ))}
      {children}
    </WallStreamsContext.Provider>
  );
};

export const WALL_STILL_WATCHING_COUNTDOWN = `The video wall will pause in ${
  IDLE_GRACE_MS / 1000
} seconds unless you confirm you're still watching.`;
export const WALL_STILL_WATCHING_PAUSED =
  "The video wall was paused after a period of inactivity to save your streaming allowance.";

/**
 * Wall-wide "Still watching?" prompt (ENG-1132 / ENG-2149). Rendered by the
 * panel/dialog that hosts the tiles. Shows during the grace window (tiles
 * still playing; the button confirms) and while the wall is idle-paused (the
 * button is Resume, which re-mints every active tile through the normal
 * play() path).
 */
export const WallIdlePrompt: React.FC<{ compact?: boolean }> = ({
  compact = false,
}) => {
  // Tolerate rendering outside a provider (unit tests render the panels bare).
  const ctx = useContext(WallStreamsContext);
  if (!ctx?.idlePaused && !ctx?.idlePrompting) return null;
  const { resumeFromIdle, idlePaused } = ctx;
  return (
    <div
      className={`flex flex-col items-center justify-center rounded border border-actuate-grey bg-[#FAFAFB] text-center font-mulish ${
        compact ? "gap-1 px-3 py-3" : "gap-2 px-6 py-5"
      }`}
      data-testid={
        idlePaused ? "wall-idle-prompt" : "wall-still-watching-prompt"
      }
      role={idlePaused ? undefined : "alertdialog"}
      aria-label="Still watching?"
    >
      <p
        className={`font-bold text-actuate-blue ${
          compact ? "text-[14px]" : "text-[20px]"
        }`}
      >
        Still watching?
      </p>
      <p className={`text-gray-600 ${compact ? "text-[11px]" : "text-[14px]"}`}>
        {idlePaused
          ? WALL_STILL_WATCHING_PAUSED
          : WALL_STILL_WATCHING_COUNTDOWN}
      </p>
      <button
        type="button"
        onClick={resumeFromIdle}
        className={`shadow-button mt-1 rounded border-2 border-actuate-orange bg-actuate-orange font-mulish text-white hover:bg-[#E67300] ${
          compact ? "px-3 py-1 text-[13px]" : "px-4 py-1.5 text-base"
        }`}
      >
        {idlePaused ? "Resume" : "I'm still watching"}
      </button>
    </div>
  );
};
